import { useEffect, useMemo, useState } from 'react';
import { applyMentionPick, detectMentionTrigger } from '../../../../lib/agent-mentions';
import { groupMentionSections, mentionsForSend, pickedMention, type GroupMentionCandidate, type PickedMention } from '../../../../lib/group-chat-actions';
import type { GroupSummary, Translate } from '../../../../lib/group-chat';
import { claimMentionOpenRefresh } from '../../../../lib/group-mention-refresh';
import { queryClient } from '../../../../lib/query-client';
import { groupMembersKey } from '../../../../lib/use-group-summary';

export interface GroupMentionsState {
  open: boolean;
  people: GroupMentionCandidate[];
  assistants: GroupMentionCandidate[];
  pick: (candidate: GroupMentionCandidate) => void;
  dismiss: () => void;
  /** The two mention lists to send with this text (people; Maestros by owner) — a deleted `@Name` is dropped. */
  idsFor: (text: string) => { mentionedMetaSiteIds: string[]; mentionedMaestroOwnerMetaSiteIds: string[] };
  clear: () => void;
}

/**
 * The group chat's `@` state (Phase 3b): the same trigger rule as the agents panel (`@` at a word boundary,
 * whitespace dismisses), but the roster is the group — People and Assistants — and SEVERAL mentions can ride one
 * post (`mentionedMetaSiteIds`). Picking inserts `@FirstName`. Inert (never open) without a summary.
 */
export function useGroupMentions({ inputText, setInputText, conversationId, summary, t }: {
  inputText: string;
  setInputText: (v: string) => void;
  conversationId: string | null;
  summary: GroupSummary | null;
  t: Translate;
}): GroupMentionsState {
  const [picked, setPicked] = useState<PickedMention[]>([]);
  const [dismissedAt, setDismissedAt] = useState<number | null>(null);
  useEffect(() => {
    setPicked([]);
    setDismissedAt(null);
  }, [conversationId]);
  const trigger = useMemo(() => (summary ? detectMentionTrigger(inputText) : null), [summary, inputText]);
  useEffect(() => {
    if (!trigger && dismissedAt !== null) setDismissedAt(null);
  }, [trigger, dismissedAt]);
  // The picker OPENING (not every keystroke in it) re-reads who can be mentioned: adding a Maestro publishes no live
  // row, so without this the picker lacks it until the chat is reopened. Throttled per group.
  const triggerOpen = !!trigger;
  useEffect(() => {
    if (triggerOpen && conversationId && claimMentionOpenRefresh(conversationId, Date.now())) {
      void queryClient.invalidateQueries({ queryKey: groupMembersKey(conversationId) });
    }
  }, [triggerOpen, conversationId]);
  const sections = useMemo(
    () => (summary && trigger ? groupMentionSections(summary, trigger.query, t) : { people: [], assistants: [] }),
    [summary, trigger, t],
  );
  return {
    open: !!trigger && trigger.start !== dismissedAt,
    people: sections.people,
    assistants: sections.assistants,
    pick: (c) => {
      if (!trigger) return;
      setInputText(applyMentionPick(inputText, trigger.start, c.name));
      setPicked((prev) => [...prev.filter((p) => p.id !== c.id), pickedMention(c)]);
    },
    dismiss: () => {
      if (trigger) setDismissedAt(trigger.start);
    },
    idsFor: (text) => mentionsForSend(picked, text),
    clear: () => setPicked([]),
  };
}
